import { LESSONS, LAB_STEPS } from '../content/lessons';
import { cloneConfig, getPreset } from '../content/presets';
import type { GateContext, Gate, LabStep, Lesson, Markup, Question } from '../content/types';
import type { ExperimentConfig } from '../simulation/types';
import { diffPaths, labelPath, sampleNear, type RunRecord } from './runRecord';

export type Answer = number | string | Record<string, string> | string[] | null;

export interface ItemRecord {
  qVersion: number;
  /** Evidence run ids (by lab step) the item was answered against. */
  runIds: Record<string, string>;
  answer: Answer;
  correct: boolean;
  attempts: number;
}

export interface GateRecord {
  gateId: string;
  gateVersion: number;
  attempts: number;
  items: Record<string, ItemRecord>;
  passed: boolean;
  passedAt: number | null;
  /** Evidence run ids at the moment of passing (retained even if runs are trimmed). */
  evidence: Record<string, string> | null;
  /** Short human summary retained if evidence traces are ever dropped. */
  summary: string | null;
}

export interface ProgressState {
  evidence: Record<string, string>;
  gates: Record<string, GateRecord>;
}

// ── Lab evidence ─────────────────────────────────────────────────────

/** The exact config a lab step loads, including inherited controllers. */
export function expectedConfig(step: LabStep, evidenceRuns: Record<string, RunRecord>): ExperimentConfig {
  const preset = getPreset(step.presetId);
  const c = cloneConfig(preset.config);
  step.setup?.(c);
  if (step.inheritControllerFrom) {
    const src = evidenceRuns[step.inheritControllerFrom];
    if (src) c.controller = structuredClone(src.config.controller);
  }
  return c;
}

const permittedPath = (path: string, permitted: string[]) => permitted.some((p) => path === p || path.startsWith(p + '.'));

/** Reasons a run does not count as evidence for a step (empty = qualifies). */
export function qualifyRun(step: LabStep, run: RunRecord, evidenceRuns: Record<string, RunRecord>): string[] {
  const reasons: string[] = [];
  const preset = getPreset(step.presetId);
  if (run.presetId !== step.presetId) return [`This run used a different experiment than "${step.title}".`];
  if (run.presetVersion !== preset.version) return ['This run used an older version of the experiment. Load the lab step again.'];
  if (run.status !== 'complete' && !(step.allowStopped && run.status === 'stopped')) {
    reasons.push(run.stopReason ? `The run stopped early: ${run.stopReason.message}` : 'The run did not reach the end.');
  }
  const expected = expectedConfig(step, evidenceRuns);
  const changed = diffPaths(expected, run.config).filter((p) => !permittedPath(p, step.permitted));
  if (changed.length) reasons.push(`Changed settings not allowed in this step: ${changed.map(labelPath).join(', ')}.`);
  if (!reasons.length && step.check) {
    const extra = step.check(run, evidenceRuns);
    if (extra) reasons.push(extra);
  }
  return reasons;
}

export function evidenceRuns(progress: ProgressState, runs: RunRecord[]): Record<string, RunRecord> {
  const byId = new Map(runs.map((r) => [r.id, r]));
  const out: Record<string, RunRecord> = {};
  for (const [stepId, runId] of Object.entries(progress.evidence)) {
    const r = byId.get(runId);
    if (r) out[stepId] = r;
  }
  return out;
}

export function buildContext(runsByStep: Record<string, RunRecord>): GateContext {
  return {
    runs: runsByStep,
    sample(stepId, t) {
      const r = runsByStep[stepId];
      if (!r) throw new Error(`No evidence for ${stepId}`);
      return sampleNear(r.trace, t);
    },
  };
}

// ── Grading ──────────────────────────────────────────────────────────

export interface Grade {
  correct: boolean;
  feedback: Markup;
}

export function parseNumber(input: string): number | null {
  const cleaned = input.trim().replace(/−/g, '-').replace(/,/g, '.').replace(/\s+/g, '');
  if (!/^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i.test(cleaned)) return null;
  const x = Number(cleaned);
  return Number.isFinite(x) ? x : null;
}

export function gradeQuestion(q: Question, answer: Answer, ctx: GateContext): Grade {
  switch (q.kind) {
    case 'numeric': {
      const x = typeof answer === 'number' ? answer : typeof answer === 'string' ? parseNumber(answer) : null;
      if (x === null) return { correct: false, feedback: `Enter a number (in ${q.unit}).` };
      const target = q.answer(ctx);
      if (Math.abs(x - target) <= q.tolerance + 1e-9) return { correct: true, feedback: q.explanation };
      const m = q.misconceptions?.find((mc) => mc.test(x, ctx));
      return { correct: false, feedback: m ? m.feedback : `Not quite (answers within ±${q.tolerance} ${q.unit} are accepted). ${q.hint}` };
    }
    case 'choice': {
      if (typeof answer !== 'string') return { correct: false, feedback: 'Choose an option.' };
      if (answer === q.correct(ctx)) return { correct: true, feedback: q.explanation };
      const opt = q.options.find((o) => o.id === answer);
      return { correct: false, feedback: opt?.feedback ?? `Not quite. ${q.hint}` };
    }
    case 'match': {
      const a = (answer && typeof answer === 'object' && !Array.isArray(answer) ? answer : {}) as Record<string, string>;
      const correct = q.correct(ctx);
      const missing = q.items.filter((i) => !a[i.id]).length;
      if (missing) return { correct: false, feedback: `Match every item (${missing} left).` };
      const right = q.items.filter((i) => a[i.id] === correct[i.id]).length;
      if (right === q.items.length) return { correct: true, feedback: q.explanation };
      return { correct: false, feedback: `${right} of ${q.items.length} matched correctly. ${q.hint}` };
    }
    case 'order': {
      const a = Array.isArray(answer) ? answer : [];
      if (a.length === q.correctOrder.length && a.every((x, i) => x === q.correctOrder[i])) return { correct: true, feedback: q.explanation };
      return { correct: false, feedback: `Not in the right order yet. ${q.hint}` };
    }
  }
}

/** Evidence run ids the question depends on. */
export function bindingFor(q: Question, evidence: Record<string, string>): Record<string, string> {
  return Object.fromEntries(q.binds.map((s) => [s, evidence[s] ?? '']));
}

/** Is a stored correct item still valid (same question version, same evidence)? */
export function itemStillCorrect(q: Question, item: ItemRecord | undefined, evidence: Record<string, string>): boolean {
  if (!item || !item.correct || item.qVersion !== q.version) return false;
  return q.binds.every((s) => item.runIds[s] === evidence[s]);
}

// ── Gate status and unlocking ────────────────────────────────────────

export interface LessonStatus {
  lesson: Lesson;
  unlocked: boolean;
  lockReason: string | null;
  missingSteps: LabStep[];
  labComplete: boolean;
  gatePassed: boolean;
  itemsRemaining: number;
}

export function isGatePassed(gate: Gate, progress: ProgressState): boolean {
  return !!progress.gates[gate.id]?.passed;
}

export function lessonStatuses(progress: ProgressState): LessonStatus[] {
  let prevPassed = true;
  let prevTitle = '';
  return LESSONS.map((lesson) => {
    const unlocked = prevPassed;
    const lockReason = unlocked ? null : `Pass the check at the end of "${prevTitle}" to unlock this module.`;
    const missingSteps = lesson.lab.steps.filter((s) => !progress.evidence[s.id]);
    const gatePassed = isGatePassed(lesson.gate, progress);
    const rec = progress.gates[lesson.gate.id];
    const itemsRemaining = gatePassed
      ? 0
      : lesson.gate.questions.filter((q) => !itemStillCorrect(q, rec?.items[q.id], progress.evidence)).length;
    prevPassed = prevPassed && gatePassed;
    prevTitle = `${lesson.number}. ${lesson.title}`;
    return { lesson, unlocked, lockReason, missingSteps, labComplete: missingSteps.length === 0, gatePassed, itemsRemaining };
  });
}

export function sandboxUnlocked(progress: ProgressState): boolean {
  return LESSONS.every((l) => isGatePassed(l.gate, progress));
}

export interface SubmitResult {
  record: GateRecord;
  grades: Record<string, Grade>;
  passed: boolean;
  blocked: string | null;
}

/**
 * Grade a gate submission. Items already correct against unchanged evidence stay complete.
 * Requires every lab step's evidence and every essential item to be correct.
 */
export function submitGate(
  lesson: Lesson,
  answers: Record<string, Answer>,
  progress: ProgressState,
  runs: RunRecord[],
  now: number,
): SubmitResult {
  const gate = lesson.gate;
  const prev = progress.gates[gate.id];
  const base: GateRecord =
    prev && prev.gateVersion === gate.version
      ? structuredClone(prev)
      : { gateId: gate.id, gateVersion: gate.version, attempts: 0, items: {}, passed: false, passedAt: null, evidence: null, summary: null };

  const missing = lesson.lab.steps.filter((s) => !progress.evidence[s.id]);
  if (missing.length) {
    return { record: base, grades: {}, passed: false, blocked: `Finish the lab first: ${missing.map((s) => s.title).join('; ')}.` };
  }
  const evRuns = evidenceRuns(progress, runs);
  const ctx = buildContext(evRuns);
  base.attempts++;
  const grades: Record<string, Grade> = {};
  for (const q of gate.questions) {
    const prevItem = base.items[q.id];
    if (itemStillCorrect(q, prevItem, progress.evidence) && answers[q.id] === undefined) {
      grades[q.id] = { correct: true, feedback: q.explanation };
      continue;
    }
    const answer = answers[q.id] ?? null;
    let grade: Grade;
    try {
      grade = gradeQuestion(q, answer, ctx);
    } catch {
      grade = { correct: false, feedback: 'This question needs a recorded lab run. Re-run the lab step it refers to.' };
    }
    grades[q.id] = grade;
    base.items[q.id] = {
      qVersion: q.version,
      runIds: bindingFor(q, progress.evidence),
      answer,
      correct: grade.correct,
      attempts: (prevItem?.qVersion === q.version ? prevItem.attempts : 0) + 1,
    };
  }
  const extra = gate.evidenceRequirement?.(ctx) ?? null;
  const allCorrect = gate.questions.every((q) => itemStillCorrect(q, base.items[q.id], progress.evidence));
  const passed = allCorrect && !extra;
  if (passed && !base.passed) {
    base.passed = true;
    base.passedAt = now;
    base.evidence = { ...Object.fromEntries(lesson.lab.steps.map((s) => [s.id, progress.evidence[s.id]])) };
    base.summary = `Passed on attempt ${base.attempts} with ${lesson.lab.steps.length} lab run(s).`;
  }
  return { record: base, grades, passed: base.passed, blocked: extra };
}

/** Which lab step (if any) a finished run should be recorded against. */
export function assignEvidence(
  run: RunRecord,
  activeStepId: string | null,
  progress: ProgressState,
  runs: RunRecord[],
): { stepId: string | null; reasons: string[]; frozen: boolean } {
  const evRuns = evidenceRuns(progress, runs);
  const candidates: string[] = [];
  if (activeStepId) candidates.push(activeStepId);
  if (!activeStepId) return { stepId: null, reasons: [], frozen: false };
  const { lesson, step } = LAB_STEPS[activeStepId] ?? {};
  if (!lesson || !step) return { stepId: null, reasons: [], frozen: false };
  if (isGatePassed(lesson.gate, progress)) {
    // Never replace evidence a passed gate relied on.
    return { stepId: null, reasons: [], frozen: true };
  }
  const reasons = qualifyRun(step, run, evRuns);
  if (reasons.length === 0) return { stepId: step.id, reasons: [], frozen: false };
  // Otherwise, see whether it happens to qualify for another still-missing step in the same lesson.
  for (const other of lesson.lab.steps) {
    if (other.id === step.id || progress.evidence[other.id]) continue;
    if (qualifyRun(other, run, evRuns).length === 0) return { stepId: other.id, reasons: [], frozen: false };
  }
  return { stepId: null, reasons, frozen: false };
}
